import axios from 'axios';
import { endInfoRead, mergeInfo, startInfoRead } from './brandStore';
import type { NoticeInfo } from './realImages';
import { createDocEntryStore } from './docStore';
import { readState, writeState } from './docState';
import type { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist';
import { ACTIVE_MODELS } from './models';
import { loadPdf, renderPdfPageToImage } from './pdfRender';
import { haltGate } from './runHalt';

// The page-range categorize run lives here, at module scope, instead of inside
// the CategoryTable component. The reader (NoticeViewer) is a React Router route,
// so navigating to another tab unmounts it and its PdfViewer, which destroys the
// PDF.js document. Holding the run here — keyed by document id, with its own
// PDF.js document — lets it keep going while the user is on another tab.

// During a range run, pages are classified in blocks of this size: one Gemini
// call sees a whole block (in order) so it keeps assembly step numbers
// consistent. Blocks run sequentially per model, carrying the previous block's
// last category forward so the numbering stays consistent across block seams.
export const BATCH_SIZE = 20;

// The width the sections call renders every page at. Wide, because the same call reads the
// shop's address off the manual, and it is printed in the document's smallest type: a host
// read one character wrong is a search of a shop that does not exist.
export const SECTIONS_PAGE_WIDTH = 1500;

// model id -> page number -> category label (or per-cell error message)
type ByModelPage = Record<string, Record<number, string>>;

// A page is an ordered top-to-bottom list of sections. Each segment is a category
// plus the `start` (0..1 from the top) where it begins; the first segment starts
// at 0. A single-section page has one segment. This supports a page that holds
// several assembly steps (e.g. 4, 5 and the start of 6), not just two.
export type Segment = { category: string; start: number };
type SegmentsMap = Record<string, Record<number, Segment[]>>;

// This phase's answers about a document, for the models it called over a page range.
//
// This used to be an ARRAY of these, browsed with a "Run 61/63" pager under the table
// — the only history in the app, and only about the classification. A run is a version
// of the whole DOCUMENT now (docState): re-running any phase opens one, carrying the
// others forward, and the pager moved to Phase 1 where it governs all of them. So this
// is one result set again, and the one it is depends on which run is on screen.
export type Run = {
  models: string[];
  from: number;
  to: number;
  categories: ByModelPage;
  reasoning: ByModelPage;
  raws: ByModelPage;
  segments: SegmentsMap;
};

export type RunSnapshot = {
  busy: null | 'range';
  progress: { done: number; total: number } | null;
  active: number;
  // This run's result set, and the maps mirrored off it so components read the
  // displayed results directly.
  run: Run | null;
  categories: ByModelPage;
  reasoning: ByModelPage;
  raws: ByModelPage;
  segments: SegmentsMap;
  runModels: string[];
  // Per-cell failures of the active run (transient, not persisted).
  cellErrors: ByModelPage;
  error: string | null;
};

// The one per-document field beyond the reactive snapshot: the index of the run
// currently being filled (-1 when idle; setResult targets it). An AbortController
// used to live here too, until it turned out that aborting a call already sent
// only throws away an answer Gemini bills for regardless — see runHalt.
const store = createDocEntryStore<RunSnapshot>((docId) => {
  return {
    snapshot: {
      busy: null,
      progress: null,
      active: 0,
      ...mirror(loadRun(docId)),
      cellErrors: {},
      error: null,
    },
  };
});

// The legacy key is still read: it held the whole history as an array, and its LAST
// entry is this document's most recent classification. The 62 before it are not
// carried into the document runs — they were answers about the classification alone,
// and a document run means every phase at once. They stay in the row, unread.
const legacyRunsKey = (docId: string) => `notice.runs.${docId}`;
const classifyKey = (docId: string) => `notice.classify.${docId}`;
// Pre-history single-result keys, migrated into one run on first load.
const legacyCatKey = (docId: string) => `notice.categories.${docId}`;
const legacyReasonKey = (docId: string) => `notice.reasoning.${docId}`;
const legacyRawsKey = (docId: string) => `notice.raws.${docId}`;
const legacySplitsKey = (docId: string) => `notice.splits.${docId}`;


// Bring a stored run up to the current shape: older runs (and the pre-history
// single result set) carried a one-boundary `splits` map instead of `segments`.
// Convert each split into a two-segment list, single categories into one segment.
type LegacySplit = { y: number; above: string; below: string };
function migrateRun(run: Run & { splits?: Record<string, Record<number, LegacySplit>> }): Run {
  if (run.segments) return run;
  const splits = run.splits || {};
  const categories = run.categories || {};
  const segments: SegmentsMap = {};
  for (const modelId of Object.keys(categories)) {
    segments[modelId] = {};
    for (const pageStr of Object.keys(categories[modelId])) {
      const page = Number(pageStr);
      const sp = splits[modelId]?.[page];
      segments[modelId][page] = sp
        ? [{ category: sp.above, start: 0 }, { category: sp.below, start: sp.y }]
        : [{ category: categories[modelId][page], start: 0 }];
    }
  }
  const { splits: _drop, ...rest } = run;
  return { ...rest, segments };
}

function loadRun(docId: string): Run | null {
  const own = readState<Run | null>(docId, classifyKey(docId), null);
  if (own) return migrateRun(own);
  // The history-as-an-array this phase used to keep: its newest entry is the answer.
  const stored = readState<Run[]>(docId, legacyRunsKey(docId), []);
  if (Array.isArray(stored) && stored.length) return migrateRun(stored[stored.length - 1]);
  // Migrate a pre-history single result set, if present.
  const categories = readState<ByModelPage>(docId, legacyCatKey(docId), {});
  if (!Object.keys(categories).length) return null;
  const migrated = migrateRun({
    models: Object.keys(categories),
    from: 1,
    to: 0,
    categories,
    reasoning: readState<ByModelPage>(docId, legacyReasonKey(docId), {}),
    raws: readState<ByModelPage>(docId, legacyRawsKey(docId), {}),
    segments: undefined as unknown as SegmentsMap,
    splits: readState<Record<string, Record<number, LegacySplit>>>(docId, legacySplitsKey(docId), {}),
  });
  writeState(docId, classifyKey(docId), migrated);
  return migrated;
}

const EMPTY: ByModelPage = {};
const EMPTY_SEGMENTS: SegmentsMap = {};

// Derive the displayed maps from this run's result set.
function mirror(r: Run | null) {
  return {
    categories: r?.categories ?? EMPTY,
    reasoning: r?.reasoning ?? EMPTY,
    raws: r?.raws ?? EMPTY,
    segments: r?.segments ?? EMPTY_SEGMENTS,
    runModels: r?.models ?? [],
    run: r,
  };
}

// Replace a document's snapshot (immutably) and notify subscribers. The displayed
// maps are always re-derived from the result set.
function update(docId: string, patch: Partial<RunSnapshot>) {
  const entry = store.getEntry(docId);
  const next = { ...entry.snapshot, ...patch };
  entry.snapshot = { ...next, ...mirror(next.run) };
  store.notify(docId);
}

// Record one (model, page) result into this document run's result set. A page is an
// ordered list of segments; its display label joins their categories with "&".
function setResult(
  docId: string,
  modelId: string,
  pageNum: number,
  segments: Segment[],
  reasoning: string,
  raw: string,
) {
  const entry = store.getEntry(docId);
  const run = entry.snapshot.run;
  if (!run) return;
  const label = segments.map((s) => s.category).join(' & ');
  const next: Run = {
    ...run,
    categories: { ...run.categories, [modelId]: { ...(run.categories[modelId] || {}), [pageNum]: label } },
    reasoning: { ...run.reasoning, [modelId]: { ...(run.reasoning[modelId] || {}), [pageNum]: reasoning } },
    raws: { ...run.raws, [modelId]: { ...(run.raws[modelId] || {}), [pageNum]: raw } },
    segments: { ...run.segments, [modelId]: { ...(run.segments[modelId] || {}), [pageNum]: segments } },
  };
  writeState(docId, classifyKey(docId), next);
  update(docId, { run: next, cellErrors: clearCell(entry.snapshot.cellErrors, modelId, pageNum) });
}

function clearCell(map: ByModelPage, modelId: string, pageNum: number): ByModelPage {
  if (map[modelId]?.[pageNum] === undefined) return map;
  const forModel = { ...map[modelId] };
  delete forModel[pageNum];
  return { ...map, [modelId]: forModel };
}

function recordCellError(docId: string, modelId: string, pageNum: number, message: string) {
  const { cellErrors } = store.getEntry(docId).snapshot;
  update(docId, {
    cellErrors: { ...cellErrors, [modelId]: { ...(cellErrors[modelId] || {}), [pageNum]: message } },
  });
}

type BatchResult = {
  segments: Segment[];
  reasoning?: string;
  raw?: string;
};

// Classify one block of pages for one model in a single call, storing results in
// the active run. The same call reads the manual's general info off the block; only the
// first model's answer is kept (`readsInfo`), since the info table has one value a field. The model sees the pages in order plus `prevCategory` (the
// category in effect just before the block), so step numbers stay consistent.
// Returns the category in effect at the bottom of the block (to carry to the next
// one), or null. A cancelled request leaves the cells untouched; any other
// failure marks every page of the block as errored for this model (no fallback).
async function classifyBlock(
  docId: string,
  modelId: string,
  pages: number[],
  images: string[],
  prevCategory: string | null,
  readsInfo: boolean,
  t: (k: string) => string,
): Promise<string | null> {
  try {
    // No abort signal: this call is billed the moment it is sent, so a halt lets it
    // land and keep its answer. Halting is checked before the NEXT one — see runHalt.
    const { data } = await axios.post<{ results: BatchResult[]; info: Partial<NoticeInfo> | null }>(
      '/api/notice/categorize-batch',
      { model: modelId, pages, images, prevCategory },
    );
    const results = data.results || [];
    if (readsInfo && data.info) mergeInfo(docId, { ...data.info, reasoning: results[0]?.reasoning || '' });
    pages.forEach((n, i) => {
      const r = results[i];
      if (!r || !r.segments?.length) return;
      setResult(docId, modelId, n, r.segments, r.reasoning || '', r.raw || '');
    });
    // Carry the bottom-most category of the last page forward to the next block.
    const last = results[results.length - 1]?.segments;
    return last?.length ? last[last.length - 1].category : prevCategory;
  } catch (e) {
    const msg =
      (axios.isAxiosError(e) && (e.response?.data as { error?: string })?.error) ||
      (e instanceof Error && e.message) ||
      t('notice.err.generic');
    pages.forEach((n) => recordCellError(docId, modelId, n, msg));
    return prevCategory;
  }
}

// The segments this model already has for a page in the run being filled, or null.
// Its presence is what makes a page "done": a resumed phase re-asks for the pages
// that have none and no others, which is the whole of why a halt costs nothing.
function doneSegments(docId: string, modelId: string, page: number): Segment[] | null {
  const segs = store.getEntry(docId).snapshot.run?.segments[modelId]?.[page];
  return segs?.length ? segs : null;
}

function bumpActive(docId: string, delta: number) {
  update(docId, { active: store.getEntry(docId).snapshot.active + delta });
}

// Build the list of page numbers to run for a contiguous range, clamped to the
// document and order-forgiving (from/to may be swapped).
function pageRange(from: number, to: number, numPages: number): number[] {
  const lo = Math.max(1, Math.min(from, to));
  const hi = Math.min(numPages, Math.max(from, to));
  const pages: number[] = [];
  for (let n = lo; n <= hi; n++) pages.push(n);
  return pages;
}

// Split a list of pages into consecutive blocks of at most `size`.
export function chunk<T>(items: T[], size: number): T[][] {
  const blocks: T[][] = [];
  for (let i = 0; i < items.length; i += size) blocks.push(items.slice(i, i + size));
  return blocks;
}

// Start a new run over a page range. It appends a fresh result set (for the
// enabled models) to the history and selects it, so previous outputs disappear
// from view, then classifies each page with a bounded worker pool.
// `resume` continues the run this document was last filling rather than opening a
// new one: a halted phase left it half-classified, and its pages are answers already
// paid for. The range and the models come from that run, not from the arguments —
// resuming has to mean the same run, or the seam between its two halves is a page
// classified under rules the rest of it never saw.
export async function startRange(
  docId: string,
  file: Blob,
  from: number,
  to: number,
  t: (k: string) => string,
  resume = false,
) {
  const entry = store.getEntry(docId);
  if (entry.snapshot.busy) return;

  // `resume` continues the result set this run already has: a halted phase left it
  // half-classified, and its pages are answers already paid for. The range and the
  // models come from it, not from the arguments — resuming has to mean the same run,
  // or the seam between its two halves is a page classified under rules the rest of it
  // never saw.
  //
  // A FRESH classification blanks the set instead. It does not need to keep the last
  // one: opening a document run copied it forward, and that copy is what the pager on
  // Phase 1 can still go back to.
  const prior = entry.snapshot.run;
  const continuing = resume && !!prior;
  const models = continuing ? prior.models : ACTIVE_MODELS.map((m) => m.id);
  const lo = continuing ? prior.from : from;
  const hi = continuing ? prior.to : to;

  const run: Run = continuing
    ? prior
    : { models, from, to, categories: {}, reasoning: {}, raws: {}, segments: {} };
  if (!continuing) writeState(docId, classifyKey(docId), run);
  startInfoRead(docId, !continuing);
  update(docId, {
    run,
    busy: 'range',
    error: null,
    progress: null,
    active: 0,
    cellErrors: {},
  });

  const halted = haltGate(docId);

  let loadingTask: PDFDocumentLoadingTask | null = null;
  try {
    const buf = await file.arrayBuffer();
    loadingTask = loadPdf(buf);
    const doc: PDFDocumentProxy = await loadingTask.promise;
    const pages = pageRange(lo, hi, doc.numPages);
    if (pages.length === 0) return;
    const total = models.length * pages.length;
    update(docId, { progress: { done: 0, total } });

    // Render each page once, shared across models: cache the promise so two
    // models awaiting the same page don't both render it.
    const renderCache = new Map<number, Promise<string | null>>();
    const renderPage = (n: number) => {
      let p = renderCache.get(n);
      if (!p) {
        p = renderPdfPageToImage(doc, n, SECTIONS_PAGE_WIDTH);
        renderCache.set(n, p);
      }
      return p;
    };

    const blocks = chunk(pages, BATCH_SIZE);
    let done = 0;
    const bump = (by: number) => update(docId, { progress: { done: (done += by), total } });

    // Each model walks its blocks in order, carrying the previous block's last
    // category forward so step numbers stay consistent across seams. The two
    // models run in parallel with each other (independent sequences).
    const runModel = async (modelId: string) => {
      let prevCategory: string | null = null;
      for (const block of blocks) {
        if (halted()) return;

        // A block this model already answered, in the run being resumed. Carry its
        // last category forward exactly as the call would have, and ask nothing: the
        // answer is bought, and re-asking is the waste that made a halt expensive.
        // Whole blocks only — a block is ONE call, so it is all-or-nothing anyway.
        const already = block.map((n) => doneSegments(docId, modelId, n));
        if (already.every(Boolean)) {
          const last = already[already.length - 1] as Segment[];
          prevCategory = last[last.length - 1].category;
          bump(block.length);
          continue;
        }

        // Render the block; a page that fails to render is surfaced as a cell
        // error and dropped from the call (the rest still go, mapped by page).
        const pageNums: number[] = [];
        const images: string[] = [];
        for (const n of block) {
          const image = await renderPage(n);
          if (halted()) return;
          if (image) {
            pageNums.push(n);
            images.push(image);
          } else {
            recordCellError(docId, modelId, n, t('notice.err.rendering'));
            bump(1);
          }
        }
        if (pageNums.length === 0) continue;
        bumpActive(docId, 1);
        try {
          // No halt check after this: the call is away and billed, so its answer is
          // written whatever happened meanwhile. The check at the top of the loop is
          // what stops the next one.
          prevCategory = await classifyBlock(
            docId, modelId, pageNums, images, prevCategory, modelId === models[0], t,
          );
        } finally {
          bumpActive(docId, -1);
        }
        bump(pageNums.length);
      }
    };

    await Promise.all(models.map(runModel));
  } catch {
    if (!halted()) update(docId, { error: t('notice.err.rendering') });
  } finally {
    update(docId, { busy: null, progress: null, active: 0 });
    endInfoRead(docId);
    void loadingTask?.destroy();
  }
}


// Browsing the runs used to live here, because this phase was the only one that kept
// any. It is docState.showRun now, driven from Phase 1: a run is a version of the
// whole document, so the pager belongs where the document does — and every phase
// follows it, not just this one.

// Non-hook read of the current run snapshot (for the "Run all" orchestrator).
export const getRun = store.get;

// Subscribe a component to a document's run state.
export const useRun = store.useSnapshot;
